/**
 * ChatQuotaBanner — in-chat pre-warning that the user's monthly token
 * quota is ≥80% consumed. Fires once per session (localStorage flag)
 * so it doesn't nag on every mount. Clicking "Upgrade" emits the
 * typed `open-plans` bus event, letting the App.tsx router switch to
 * the profile tab where PlansView opens itself.
 *
 * Phase G6 — the old UX was: user hits message N+1, paywall modal
 * appears unexpectedly. The banner gives a quieter heads-up one
 * message earlier, so "oh I'm nearly out" is visible before the hard
 * stop.
 */

import { AlertTriangle, Zap } from 'lucide-react';
import { useMemo } from 'react';
import Button from '../../core/ui/Button';
import { emit as emitAppEvent } from '../../core/events/bus';
import { useCurrentUser } from '../../core/auth/UserProvider';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { chatQuotaFraction } from '../../userService';
import './ChatQuotaBanner.css';

const WARN_THRESHOLD = 0.8;
const DISMISS_STORAGE_KEY_PREFIX = 'chiefy.chatQuotaBanner.dismissed.';

function periodKey(user: { access: { period_start?: string | null } | null } | null): string {
    // Tie dismissal to the billing period — a new period resets the
    // banner so a user who dismissed last month sees it again once they
    // cross 80% in the new month.
    return user?.access?.period_start ?? 'default';
}

function wasDismissedThisPeriod(key: string): boolean {
    try {
        return localStorage.getItem(DISMISS_STORAGE_KEY_PREFIX + key) === 'true';
    } catch {
        return false;
    }
}

function markDismissed(key: string) {
    try {
        localStorage.setItem(DISMISS_STORAGE_KEY_PREFIX + key, 'true');
    } catch { /* quota */ }
}

export default function ChatQuotaBanner() {
    const user = useCurrentUser();
    const isOnline = useNetworkStatus();
    const fraction = chatQuotaFraction(user);
    const key = useMemo(() => periodKey(user), [user]);

    // Banner precedence: OfflineBanner wins when the user is offline
    // (that's the more actionable signal — nothing else will work
    // until they reconnect). Quota banner suppresses itself so the
    // user isn't hit with two competing CTAs.
    if (!isOnline) return null;
    if (fraction === null || fraction < WARN_THRESHOLD) return null;
    if (wasDismissedThisPeriod(key)) return null;

    const pct = Math.round(fraction * 100);
    const isExhausted = fraction >= 1;

    return (
        <div
            className={`chat-quota-banner${isExhausted ? ' chat-quota-banner--exhausted' : ''}`}
            role={isExhausted ? 'alert' : 'status'}
        >
            <AlertTriangle size={16} aria-hidden="true" />
            <span className="chat-quota-banner__text">
                {isExhausted
                    ? "You've used all your chat tokens this month. Upgrade to keep the conversation going."
                    : `You've used ${pct}% of your chat tokens this month.`}
            </span>
            <div className="chat-quota-banner__actions">
                <Button
                    variant="primary"
                    size="sm"
                    icon={<Zap size={14} />}
                    onClick={() => emitAppEvent('open-plans', { reason: 'quota' })}
                >
                    Upgrade
                </Button>
                {!isExhausted && (
                    <button
                        type="button"
                        className="chat-quota-banner__dismiss"
                        onClick={() => { markDismissed(key); window.location.reload(); }}
                        aria-label="Dismiss warning for this billing period"
                    >
                        Dismiss
                    </button>
                )}
            </div>
        </div>
    );
}
